import { Box } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toNumber } from 'shared/lib/number/number';
import {
    formatReportAxisCurrency,
    formatReportCurrency,
} from 'shared/lib/reportFormatters';
import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
} from 'widgets/ComposableReportWidget';
import {
    buildSpendRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

import { CategoryGlCodesTooltip } from './CategoryGlCodesTooltip';

import {
    formatGlCodeLabel,
    uniqueGlCodeValues,
    visibleGlCodes,
} from '../model/lib/categoryGlCodes';
import {
    nextPropertyTableSort,
    sortPropertyWidgetItems,
} from '../model/lib/sortPropertyWidgetItems';
import {
    COMPACT_GL_CODE_COLUMN_WIDTH_PX,
    COMPACT_LABEL_COLUMN_WIDTH_PX,
    COMPACT_SPEND_AMOUNT_COLUMN_WIDTH_PX,
    COMPACT_SPEND_PERCENT_COLUMN_WIDTH_PX,
    SPEND_AMOUNT_COLUMN_WIDTH_PX,
    SPEND_COUNT_COLUMN_WIDTH_PX,
    SPEND_PERCENT_COLUMN_WIDTH_PX,
    fixedColumnWidth,
} from '../model/lib/spendTableColumnWidths';

import type {
    PropertyTableSortDirection,
    PropertyTableSortField,
} from '../model/lib/sortPropertyWidgetItems';
import type {
    SpendCategoryGlCodeDto,
    SpendCategoryRowDto,
} from 'entities/ReportSpendSummary';

const TOP_CATEGORIES_COUNT = 10;
const TABLE_INITIAL_LIMIT = 5;
const GL_CODE_COLUMN_WIDTH_PX = 140;
const CATEGORY_LABEL_MIN_WIDTH_PX = 200;

type SpendSummaryCategoriesTableColumnId =
    | 'category'
    | 'glCode'
    | 'orders'
    | 'items'
    | 'totalSpend'
    | 'spend'
    | 'percentOfTotal'
    | 'percentSpend';

function categoryToWidgetItem(
    dto: SpendCategoryRowDto,
    index: number,
    uncategorizedLabel: string,
    glLabel: string,
): ReportWidgetItem {
    const id = dto.categoryId ?? `category-${index}`;
    const label = dto.categoryTitle?.trim() || uncategorizedLabel;
    const spendNum = toNumber(dto.totalSpend);
    const glCodes = visibleGlCodes(dto.glCodes);
    return {
        id,
        label,
        primaryValue: spendNum,
        secondaryValues: {
            label,
            glCode: glLabel,
            totalSpend: dto.totalSpend,
            orderCount: dto.orderCount,
            itemCount: dto.itemCount,
            percentOfTotal: dto.percentOfTotal,
        },
        meta: {
            categoryId: dto.categoryId,
            categoryTitle: dto.categoryTitle,
            glCodes,
        },
    };
}

interface SpendSummaryCategoriesSectionProps {
    byCategory: SpendCategoryRowDto[];
    loading?: boolean;
    disableChartAnimation?: boolean;
    showBottomBorder?: boolean;
    filtersKey?: string;
    tableOnly?: boolean;
    hideZeroSpend?: boolean;
    tableColumnIds?: SpendSummaryCategoriesTableColumnId[];
    onCategorySelect?: (payload: ReportWidgetInteractionPayload) => void;
}

export const SpendSummaryCategoriesSection = memo(
    ({
        byCategory,
        loading = false,
        disableChartAnimation = false,
        showBottomBorder = true,
        filtersKey,
        tableOnly = false,
        hideZeroSpend = false,
        tableColumnIds,
        onCategorySelect,
    }: SpendSummaryCategoriesSectionProps) => {
        const { t } = useTranslation();
        const [sortField, setSortField] =
            useState<PropertyTableSortField>('spend');
        const [sortDirection, setSortDirection] =
            useState<PropertyTableSortDirection>('desc');

        const rankedItems: ReportWidgetItem[] = useMemo(() => {
            const uncategorizedLabel = t('spendSummary.uncategorized');
            const rows = hideZeroSpend
                ? byCategory.filter((row) => toNumber(row.totalSpend) > 0)
                : byCategory;
            return [...rows]
                .sort((a, b) => toNumber(b.totalSpend) - toNumber(a.totalSpend))
                .map((dto, i) =>
                    categoryToWidgetItem(
                        dto,
                        i,
                        uncategorizedLabel,
                        formatGlCodeLabel(
                            visibleGlCodes(dto.glCodes),
                            (count) => t('spendSummary.glCodeCount', { count }),
                        ),
                    ),
                );
        }, [byCategory, hideZeroSpend, t]);

        const items = useMemo(
            () =>
                sortPropertyWidgetItems(rankedItems, sortField, sortDirection),
            [rankedItems, sortField, sortDirection],
        );

        const chartItems = useMemo(() => {
            if (tableOnly) {
                return undefined;
            }
            return rankedItems
                .filter((item) => toNumber(item.primaryValue) > 0)
                .slice(0, TOP_CATEGORIES_COUNT);
        }, [rankedItems, tableOnly]);

        const handleInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                onCategorySelect?.(payload);
            },
            [onCategorySelect],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<
                SpendSummaryCategoriesTableColumnId,
                ReportWidgetColumnConfig
            > = {
                category: {
                    id: 'category',
                    label: t('Category'),
                    valueKey: 'label',
                    format: 'text',
                    align: 'left',
                    bold: true,
                    minWidth: tableOnly
                        ? COMPACT_LABEL_COLUMN_WIDTH_PX
                        : CATEGORY_LABEL_MIN_WIDTH_PX,
                    allowWrap: true,
                    sortable: true,
                },
                glCode: {
                    id: 'glCode',
                    label: t('GL Code'),
                    valueKey: 'glCode',
                    format: 'text',
                    align: 'left',
                    ...fixedColumnWidth(
                        tableOnly
                            ? COMPACT_GL_CODE_COLUMN_WIDTH_PX
                            : GL_CODE_COLUMN_WIDTH_PX,
                    ),
                    showTooltipIcon: true,
                    getCellTooltip: (item) => {
                        const codes = item.meta?.glCodes;
                        if (
                            !Array.isArray(codes) ||
                            uniqueGlCodeValues(codes).length < 2
                        ) {
                            return null;
                        }
                        return (
                            <CategoryGlCodesTooltip
                                codes={codes as SpendCategoryGlCodeDto[]}
                            />
                        );
                    },
                },
                orders: {
                    id: 'orders',
                    label: t('Orders'),
                    valueKey: 'orderCount',
                    format: 'number',
                    align: 'right',
                    ...fixedColumnWidth(SPEND_COUNT_COLUMN_WIDTH_PX),
                },
                items: {
                    id: 'items',
                    label: t('Items'),
                    valueKey: 'itemCount',
                    format: 'number',
                    align: 'right',
                    ...fixedColumnWidth(SPEND_COUNT_COLUMN_WIDTH_PX),
                },
                totalSpend: {
                    id: 'totalSpend',
                    label: t('spendSummary.totalSpend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                    sortable: true,
                    ...fixedColumnWidth(
                        tableOnly
                            ? COMPACT_SPEND_AMOUNT_COLUMN_WIDTH_PX
                            : SPEND_AMOUNT_COLUMN_WIDTH_PX,
                    ),
                },
                spend: {
                    id: 'spend',
                    label: t('spendSummary.spend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                    sortable: true,
                    ...fixedColumnWidth(
                        tableOnly
                            ? COMPACT_SPEND_AMOUNT_COLUMN_WIDTH_PX
                            : SPEND_AMOUNT_COLUMN_WIDTH_PX,
                    ),
                },
                percentOfTotal: {
                    id: 'percentOfTotal',
                    label: t('spendSummary.percentOfTotal'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                    ...fixedColumnWidth(
                        tableOnly
                            ? COMPACT_SPEND_PERCENT_COLUMN_WIDTH_PX
                            : SPEND_PERCENT_COLUMN_WIDTH_PX,
                    ),
                },
                percentSpend: {
                    id: 'percentSpend',
                    label: t('spendSummary.percentSpend'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                    ...fixedColumnWidth(
                        tableOnly
                            ? COMPACT_SPEND_PERCENT_COLUMN_WIDTH_PX
                            : SPEND_PERCENT_COLUMN_WIDTH_PX,
                    ),
                },
            };

            const defaultOrder: SpendSummaryCategoriesTableColumnId[] =
                tableOnly
                    ? [
                          'category',
                          'glCode',
                          'orders',
                          'items',
                          'totalSpend',
                          'percentOfTotal',
                      ]
                    : ['category', 'glCode', 'totalSpend', 'percentSpend'];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            return orderedIds
                .map((id) => allColumnsById[id])
                .filter((column): column is ReportWidgetColumnConfig =>
                    Boolean(column),
                );
        }, [t, tableColumnIds, tableOnly]);

        const spendColumnId =
            tableColumnIds?.includes('spend') &&
            !tableColumnIds.includes('totalSpend')
                ? 'spend'
                : 'totalSpend';

        const handleSort = useCallback(
            (columnId: string) => {
                const next = nextPropertyTableSort({
                    field: sortField,
                    direction: sortDirection,
                    columnId: columnId === 'category' ? 'property' : columnId,
                });
                setSortField(next.field);
                setSortDirection(next.direction);
            },
            [sortField, sortDirection],
        );

        const config: ReportWidgetConfig = useMemo(() => {
            const tooltipRowsBuilder: ReportWidgetTooltipRowsBuilder = (
                args,
            ) => {
                const resolved = getTooltipPointFromPayload(args);
                const item = resolved?.item;

                if (!item) {
                    return null;
                }

                const spendNum = toNumber(item.primaryValue);
                const tooltipRows: ReportWidgetTooltipRowConfig[] = [
                    {
                        id: 'header',
                        variant: 'header',
                        label: item.label,
                        boldLabel: true,
                        mutedLabel: true,
                        marginBottom: 4,
                    },
                    buildSpendRow({
                        id: 'spend',
                        label: t('spendSummary.spend'),
                        amount: spendNum,
                        percent: item.secondaryValues?.percentOfTotal,
                        total: args.total,
                        valueFormatter: args.valueFormatter,
                        valueColor: resolved.color ?? 'metrics.base',
                    }),
                    {
                        id: 'glCode',
                        variant: 'labelValue',
                        label: t('spendSummary.glCode'),
                        value: String(item.secondaryValues?.glCode ?? '-'),
                    },
                ];

                return tooltipRows;
            };

            return {
                widgetId: 'spend-summary-categories',
                title: t('spendSummary.categoriesSectionTitle'),
                modelLabel: t('spendSummary.modelLabelCategories'),
                layout: 'stacked',
                visualization: {
                    enabled: !tableOnly,
                    type: 'bar',
                    options: {
                        colors: ['hsl(221, 83%, 53%)'],
                        showLegend: false,
                        valueFormatter: formatReportCurrency,
                        yAxisTickFormatter: formatReportAxisCurrency,
                        xAxisTickWrap: {
                            maxLines: 2,
                            maxCharsPerLine: 22,
                        },
                        tooltipRowsBuilder,
                    },
                },
                pagination: {
                    mode: 'client',
                    pageSize: TABLE_INITIAL_LIMIT,
                    revealAllOnLoadMore: true,
                },
                table: {
                    enabled: true,
                    columns: tableColumns,
                    options: {
                        loadMoreVariant: 'link',
                        sortColumnId:
                            sortField === 'name' ? 'category' : spendColumnId,
                        sortDirection,
                        onSort: handleSort,
                    },
                },
                onRowClick: onCategorySelect ? handleInteraction : undefined,
                onChartItemClick:
                    !tableOnly && onCategorySelect
                        ? handleInteraction
                        : undefined,
            };
        }, [
            t,
            tableColumns,
            tableOnly,
            onCategorySelect,
            handleInteraction,
            sortField,
            spendColumnId,
            sortDirection,
            handleSort,
        ]);

        return (
            <Box
                data-testclass="spend-summary-categories-section"
                data-pdf-section="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    key={filtersKey}
                    config={config}
                    items={items}
                    chartItems={chartItems}
                    loading={loading}
                    isPrinting={disableChartAnimation}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SpendSummaryCategoriesSection.displayName = 'SpendSummaryCategoriesSection';
